import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import * as React from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { acceptShare, rejectShare } from '../../../../service';
import { createShareListManager } from '../../../../utils/shareMutations';
import { sanitizeHtml } from '../../../../components-mobile/sanitizeHtml';
import PreviewFiles from '../../../FeedbacksComponents/AnswersFrame/PreviewFiles';
import closeIcon from '../../../../static/img/closecircle.svg';
import acceptIcon from '../../../../static/img/accept-icon.svg';
import rejectIcon from '../../../../static/icons/red-cross.svg';
import {
  isRichTextEmpty,
  sanitizeStimulusHtml,
  stripHtml,
} from '../../../Stimulus/stimulusHelpers';

export default function TaskPreviewDialog({
  task,
  setTaskPreviewDialog,
  shareId,
  resourceType,
  resourceCategory,
  onAcceptSuccess,
  successConfig,
  onClose,
  useNotificationsQuery = false,
}) {
  const [open, setOpen] = React.useState(true);
  const queryClient = useQueryClient();
  
  const handleClose = () => {
    setOpen(false);
    setTaskPreviewDialog(false);
    if (onClose) {
      onClose();
    }
  };

  const shareListManager = useNotificationsQuery ? null : createShareListManager(queryClient, resourceCategory);

  const markNotificationAsAccepted = async ({ id: shareIdToAccept }) => {
    await queryClient.cancelQueries({ queryKey: ['notifications'] });
    const previousNotifications = queryClient.getQueryData(['notifications']);
    if (previousNotifications) {
      const updatedNotifications = previousNotifications?.map((notification) => {
        if (notification.sharedDocument?.id === shareIdToAccept || notification.id === shareIdToAccept) {
          return { ...notification, status: 'ACCEPTED' };
        }
        return notification;
      });
      queryClient.setQueryData(['notifications'], updatedNotifications);
    }
    return { previousNotifications };
  };

  const markNotificationAsRejected = async ({ id: shareIdToReject }) => {
    await queryClient.cancelQueries({ queryKey: ['notifications'] });
    const previousNotifications = queryClient.getQueryData(['notifications']);
    if (previousNotifications) {
      const updatedNotifications = previousNotifications?.map((notification) => {
        if (notification.sharedDocument?.id === shareIdToReject || notification.id === shareIdToReject) {
          return { ...notification, status: 'REJECTED' };
        }
        return notification;
      });
      queryClient.setQueryData(['notifications'], updatedNotifications);
    }
    return { previousNotifications };
  };

  const restoreNotificationsList = (error, actionData, savedState, errorMessage = 'Failed to process share') => {
    console.error(errorMessage, error);
    console.error('Error details:', error.response || error.message);
    if (savedState?.previousNotifications) {
      queryClient.setQueryData(['notifications'], savedState.previousNotifications);
    }
    queryClient.refetchQueries({ queryKey: ['notifications'] });
    queryClient.refetchQueries({ queryKey: ['shares'] });
  };

  const acceptShareRequest = useMutation({
    mutationFn: async ({ id: shareIdToAccept }) => {
      return await acceptShare(shareIdToAccept);
    },
    onMutate: useNotificationsQuery ? markNotificationAsAccepted : shareListManager?.removeShareFromList,
    onError: (error, actionData, savedState) => {
      if (useNotificationsQuery) {
        restoreNotificationsList(error, actionData, savedState, 'Failed to accept shared task:');
      } else {
        shareListManager?.restoreShareList(error, actionData, savedState, 'Failed to accept shared task:');
        queryClient.refetchQueries({ queryKey: ['notifications'] });
      }
    },
    onSuccess: async (responseData) => {
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['shares'] });
      if (resourceType === 'TASK') {
        queryClient.invalidateQueries({ queryKey: ['completedTasks'] });
        queryClient.invalidateQueries({ queryKey: ['assignments'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }
      if (onAcceptSuccess) {
        onAcceptSuccess(responseData, { id: shareId, resourceType });
      }
      handleClose();
    },
  });

  const rejectShareRequest = useMutation({
    mutationFn: async ({ id: shareIdToReject }) => {
      return await rejectShare(shareIdToReject);
    },
    onMutate: useNotificationsQuery ? markNotificationAsRejected : shareListManager?.removeShareFromList,
    onError: (error, actionData, savedState) => {
      if (useNotificationsQuery) {
        restoreNotificationsList(error, actionData, savedState, 'Failed to reject shared task:');
      } else {
        shareListManager?.restoreShareList(error, actionData, savedState, 'Failed to reject shared task:');
        queryClient.refetchQueries({ queryKey: ['notifications'] });
      }
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['shares'] });
      handleClose();
    },
  });

  const handleAccept = () => {
    if (!shareId) {
      return;
    }
    acceptShareRequest.mutate({ id: shareId });
  };

  const handleReject = () => {
    if (!shareId) {
      return;
    }
    rejectShareRequest.mutate({ id: shareId });
  };

  const shouldShowActionButtons = shareId != null;

  if (!task) {
    return null;
  }

  const getFeedbackMethodLabel = (reviewedBy) => {
    if (reviewedBy === 'TEACHER') return 'Teacher';
    if (reviewedBy === 'JEDDAI_ASSISTED') return 'JeddAI Assisted';
    if (reviewedBy === 'P2P') return 'Peer to Peer';
    return reviewedBy;
  };

  const renderStimulus = (question) => {
    if (!question) {
      return null;
    }

    const elements = [];
    const stimulusText = question.stimulusText;
    const hasRichText = !isRichTextEmpty(stimulusText || '');
    if (hasRichText) {
      const plainText = stripHtml(stimulusText);
      elements.push(
        <FieldBox key={`stimulus-text-${question.serialNumber}`}>
          <FieldRow>
            <StimulusLabel>Stimulus:</StimulusLabel>
            <StimulusTextContainer>
              <StimulusText
                dangerouslySetInnerHTML={{
                  __html: sanitizeStimulusHtml(stimulusText),
                }}
              />
              {plainText.length > 300 && (
                <ReadMoreLink>Read more</ReadMoreLink>
              )}
            </StimulusTextContainer>
          </FieldRow>
        </FieldBox>,
      );
    }

    const stimulusFiles = Array.isArray(question.stimulusFiles)
      ? question.stimulusFiles
      : [];
    if (stimulusFiles.length > 0) {
      elements.push(
        <FieldBox key={`stimulus-files-${question.serialNumber}`}>
          <FieldRow>
            <StimulusLabel>Stimulus Files:</StimulusLabel>
            <StimulusFilesList>
              {stimulusFiles.map((file, index) => {
                if (!file || !file.url) {
                  return null;
                }
                const fileName = file.name || file.url.split('/').pop();
                const isPdf =
                  file.fileType === 'PDF' ||
                  file.mimeType === 'application/pdf' ||
                  file.url.toLowerCase().includes('.pdf');
                return (
                  <StimulusFileRow key={file.id || `${file.url}-${index}`}>
                    {isPdf ? (
                      <StimulusLink
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <PdfIcon src="/static/icons/pdf-icon.svg" alt="PDF" />
                        {fileName}
                      </StimulusLink>
                    ) : (
                      <StimulusImageContainer>
                        <PreviewFiles
                          id={`stimulus-${question.serialNumber}-${index}`}
                          url={file.url}
                        />
                      </StimulusImageContainer>
                    )}
                  </StimulusFileRow>
                );
              })}
            </StimulusFilesList>
          </FieldRow>
        </FieldBox>,
      );
    }

    if (elements.length > 0) {
      return <>{elements}</>;
    }

    const legacyUrl = question.stimulusUrl || question.fileUrl;
    if (
      legacyUrl &&
      (legacyUrl.startsWith('http') || legacyUrl.startsWith('/'))
    ) {
      const isPdf =
        legacyUrl.toLowerCase().endsWith('.pdf') ||
        legacyUrl.toLowerCase().includes('.pdf');
      const fileName = question.stimulusFileName || legacyUrl.split('/').pop();
      if (fileName) {
        return (
          <FieldBox>
            <FieldRow>
              <StimulusLabel>Stimulus:</StimulusLabel>
              {isPdf ? (
                <StimulusLink
                  href={legacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PdfIcon src="/static/icons/pdf-icon.svg" alt="PDF" />
                  {fileName}
                </StimulusLink>
              ) : (
                <StimulusImageContainer>
                  <PreviewFiles
                    id={`stimulus-${question.serialNumber}`}
                    url={legacyUrl}
                  />
                </StimulusImageContainer>
              )}
            </FieldRow>
          </FieldBox>
        );
      }
    }

    const legacyText =
      typeof question.stimulus === 'string' &&
      !question.stimulus.startsWith('http') &&
      !question.stimulus.startsWith('/')
        ? question.stimulus
        : null;
    if (legacyText && legacyText.trim().length > 0) {
      const truncatedText =
        legacyText.length > 300 ? `${legacyText.substring(0, 300)}...` : legacyText;
      return (
        <FieldBox>
          <FieldRow>
            <StimulusLabel>Stimulus:</StimulusLabel>
            <StimulusTextContainer>
              <StimulusText>{truncatedText}</StimulusText>
              {legacyText.length > 300 && <ReadMoreLink>Read more</ReadMoreLink>}
            </StimulusTextContainer>
          </FieldRow>
        </FieldBox>
      );
    }

    return null;
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
      >
        <StyledDialogContent>
          <DialogHeader>
            {task?.title && task.title.trim() && <TaskTitle>{task.title}</TaskTitle>}
            <CloseButton onClick={handleClose}>
              <CloseIcon src={closeIcon} alt="Close" />
            </CloseButton>
          </DialogHeader>
          
          <ScrollableContent>
            <QuestionsScrollContainer>
              {task?.questions && Array.isArray(task.questions) && task.questions.length > 0 ? (
                <QuestionsSection>
                  <QuestionsTitle>Questions</QuestionsTitle>
                  {task.questions.map((question, index) => (
                    <QuestionBlock key={question.serialNumber || index}>
                      <QuestionHeader>
                        QUESTION {question.serialNumber || index + 1}
                      </QuestionHeader>
                      
                      {question.type && question.type.trim() && (
                        <FieldBox>
                          <FieldRow>
                            <QuestionTypeLabel>Type:</QuestionTypeLabel>
                            <QuestionTypeValue>{question.type}</QuestionTypeValue>
                          </FieldRow>
                        </FieldBox>
                      )}
                      
                      {question.question && question.question.trim() && (
                        <FieldBox>
                          <FieldRow>
                            <TaskDescriptionLabel>Task Description:</TaskDescriptionLabel>
                            {/* Question text is teacher-written Quill HTML from
                                another user: render it formatted, but only
                                through the allowlist sanitizer (JED-1279).
                                No images: the sender's image would load in
                                the recipient's browser before they accept,
                                as a tracking pixel. */}
                            <TaskDescriptionText
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(question.question, {
                                  preserveRichContent: true,
                                  dropImages: true,
                                }),
                              }}
                            />
                          </FieldRow>
                        </FieldBox>
                      )}
                      
                      {renderStimulus(question)}
                      
                      {question.focusAreas && Array.isArray(question.focusAreas) && question.focusAreas.filter(focusArea => (focusArea.title && focusArea.title.trim()) || (focusArea.name && focusArea.name.trim())).length > 0 && (
                        <FieldBox>
                          <FieldRow>
                            <FocusAreasLabel>Focus Areas:</FocusAreasLabel>
                            <FocusAreasContainer>
                              {question.focusAreas
                                .filter(focusArea => (focusArea.title && focusArea.title.trim()) || (focusArea.name && focusArea.name.trim()))
                                .map((focusArea, faIndex) => (
                                  <FocusAreaPill key={focusArea.id || faIndex} color={focusArea.color}>
                                    <FocusAreaIcon color={focusArea.color} />
                                    <FocusAreaText>{focusArea.title || focusArea.name}</FocusAreaText>
                                  </FocusAreaPill>
                                ))}
                            </FocusAreasContainer>
                          </FieldRow>
                        </FieldBox>
                      )}
                      
                      {question.markingCriteria && question.markingCriteria.title && question.markingCriteria.title.trim() && (
                        <FieldBox>
                          <FieldRow>
                            <RubricsLabel>Rubrics:</RubricsLabel>
                            <RubricsText>
                              {question.markingCriteria.title}
                            </RubricsText>
                          </FieldRow>
                        </FieldBox>
                      )}
                      
                      {question.commentBankId && (question.commentBank?.title?.trim() || question.commentBank?.name?.trim() || question.commentBankTitle?.trim()) && (
                        <FieldBox>
                          <FieldRow>
                            <CommentBanksLabel>Comment Banks:</CommentBanksLabel>
                            <CommentBanksText>
                              {question.commentBank?.title || question.commentBank?.name || question.commentBankTitle}
                            </CommentBanksText>
                          </FieldRow>
                        </FieldBox>
                      )}
                    </QuestionBlock>
                  ))}
                </QuestionsSection>
              ) : (
                <EmptyStateMessage>No questions available for this task.</EmptyStateMessage>
              )}
            </QuestionsScrollContainer>
              
            {task?.reviewedBy && (
              <FeedbackMethodSection>
                <FeedbackMethodTitle>Feedback Method</FeedbackMethodTitle>
                <FeedbackMethodField>
                  {getFeedbackMethodLabel(task.reviewedBy)}
                </FeedbackMethodField>
              </FeedbackMethodSection>
            )}
          </ScrollableContent>
        
          {shouldShowActionButtons && (
            <ActionButtonsContainer>
              <RejectButton 
                onClick={handleReject}
                disabled={acceptShareRequest.isPending || rejectShareRequest.isPending}
              >
                <img src={rejectIcon} alt="Reject" />
                Reject
              </RejectButton>
              <AcceptButton 
                onClick={handleAccept}
                disabled={acceptShareRequest.isPending || rejectShareRequest.isPending}
              >
                <img src={acceptIcon} alt="Accept" />
                Accept
              </AcceptButton>
            </ActionButtonsContainer>
          )}
        </StyledDialogContent>
      </StyledDialog>
    </>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiPaper-root {
    width: 750px;
    max-height: 500px;
    border-radius: 12px;
    overflow: hidden;
    background-color: #ffffff;
  }
  
  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 0;
  max-height: 800px;
  overflow: hidden;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    width: 0px;
    display: none;
  }
  
  &::-webkit-scrollbar-track {
    display: none;
  }
  
  &::-webkit-scrollbar-thumb {
    display: none;
  }
  
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  background: #ffffff;
`;

const TaskTitle = styled.h2`
  color: #000000;
  font-size: 24px;
  font-family: IBM Plex Sans;
  font-weight: 700;
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.7;
  }
`;

const CloseIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const QuestionsScrollContainer = styled.div`
  padding: 24px;
  background: #F8EEFF;
  margin: 10px;
  border-radius: 8px;
  border: 1px solid rgba(201, 198, 204, 0.3);
`;

const QuestionsSection = styled.div`
  width: 100%;
`;

const QuestionsTitle = styled.h3`
  color: #000000;
  font-size: 20px;
  font-family: IBM Plex Sans;
  font-weight: 600;
  margin-bottom: 20px;
`;

const QuestionBlock = styled.div`
  background: #F3E1FF;

  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

const FieldBox = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QuestionHeader = styled.div`
  color: #7200E0;
  font-size: 18px;
  font-family: IBM Plex Sans;
  font-weight: 500;
  margin-bottom: 12px;
`;

const QuestionTypeLabel = styled.div`
  color: #1e252a;
  font-size: 14px;
  font-family: IBM Plex Sans;
  white-space: nowrap;
  flex-shrink: 0;
`;

const QuestionTypeValue = styled.div`
  color: #1e252a;
  font-size: 14px;
  font-family: IBM Plex Sans;
  font-weight: 500;
  opacity: 0.7;
  flex: 1;
`;

const TaskDescriptionLabel = styled.div`
  color: #1e252a;
  font-size: 14px;
  font-family: IBM Plex Sans;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TaskDescriptionText = styled.div`
  color: #1e252a;
  font-size: 16px;
  font-family: IBM Plex Sans;
  font-weight: 500;
  line-height: 1.5;
  flex: 1;

  p {
    margin: 0;
  }
  ul,
  ol {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }
  ul,
  li[data-list='bullet'] {
    list-style-type: disc;
  }
  ol {
    list-style-type: decimal;
  }
  /* The global meyer-reset sets these to font: inherit and
     vertical-align: baseline, which made them look like plain text. */
  strong,
  b {
    font-weight: bold;
  }
  em,
  i {
    font-style: italic;
  }
  sub,
  sup {
    font-size: 0.75em;
    line-height: 0;
  }
  sub {
    vertical-align: sub;
  }
  sup {
    vertical-align: super;
  }
  .ql-align-center {
    text-align: center;
  }
  .ql-align-right {
    text-align: right;
  }
  .ql-align-justify {
    text-align: justify;
  }
`;

const StimulusLabel = styled.div`
  color: #1e252a;
  font-size: 14px;
  font-family: IBM Plex Sans;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

const StimulusImageContainer = styled.div`
  flex: 1;
  border-radius: 8px;
  overflow: hidden;
`;

const StimulusFilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const StimulusFileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StimulusLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7200e0;
  font-size: 16px;
  font-family: IBM Plex Sans;
  font-weight: 500;
  text-decoration: none;
  flex: 1;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PdfIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const StimulusTextContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
  flex-wrap: wrap;
`;

const StimulusText = styled.div`
  color: #1e252a;
  font-size: 16px;
  font-family: IBM Plex Sans;
  font-weight: 400;
  line-height: 1.5;
  white-space: pre-wrap;

  ul, ol {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }
  ul {
    list-style-type: disc;
  }
  ol {
    list-style-type: decimal;
  }
  /* Quill 2 keeps bullet items in an <ol> and marks them data-list="bullet".
     A bullet does not count towards the numbering, as in the editor. */
  li[data-list='bullet'] {
    list-style-type: disc;
    counter-increment: list-item 0;
  }
  strong, b {
    font-weight: bold;
  }
  em, i {
    font-style: italic;
  }
  u {
    text-decoration: underline;
  }
  s {
    text-decoration: line-through;
  }
  .ql-align-center {
    text-align: center;
  }
  .ql-align-right {
    text-align: right;
  }
  .ql-align-justify {
    text-align: justify;
  }
  .ql-size-small {
    font-size: 0.75em;
  }
  .ql-size-large {
    font-size: 1.5em;
  }
  .ql-size-huge {
    font-size: 2.5em;
  }
`;

const ReadMoreLink = styled.div`
  color: #7200e0;
  font-size: 14px;
  font-family: IBM Plex Sans;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FocusAreasLabel = styled.div`
  color: #1e252a;
  font-size: 14px;
  font-family: IBM Plex Sans;
  white-space: nowrap;
  flex-shrink: 0;
`;

const FocusAreasContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
`;

const FocusAreaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #F5F5F5;
  border: 1px solid #C9C6CC;
  border-radius: 24px;
`;

const FocusAreaIcon = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 1px solid rgba(178, 174, 183, 0.5);
  flex-shrink: 0;
`;

const FocusAreaText = styled.div`
  font-size: 14px;
  font-family: IBM Plex Sans;
  font-weight: 500;
  color: #1e252a;
`;

const RubricsLabel = styled.div`
  color: #1e252a;
  font-size: 14px;
  font-family: IBM Plex Sans;
  white-space: nowrap;
  flex-shrink: 0;
`;

const RubricsText = styled.div`
  color: #1e252a;
  font-size: 16px;
  font-family: IBM Plex Sans;
  font-weight: 400;
  flex: 1;
`;

const CommentBanksLabel = styled.div`
  color: #1e252a;
  font-size: 14px;
  font-family: IBM Plex Sans;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CommentBanksText = styled.div`
  color: #1e252a;
  font-size: 16px;
  font-family: IBM Plex Sans;
  font-weight: 500;
  flex: 1;
`;

const FeedbackMethodSection = styled.div`
  padding: 24px;
  margin:10px;
  background: #F8EEFF;
  border-radius: 8px;
  border: 1px solid rgba(201, 198, 204, 0.3);
`;

const FeedbackMethodTitle = styled.div`
  color: #000000;
  font-size: 18px;
  font-family: IBM Plex Sans;
  font-weight: 600;
  margin-bottom: 12px;
`;

const FeedbackMethodField = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background-color: #ffffff;
  color: #1e252a;
  font-size: 16px;
  font-family: IBM Plex Sans;
  font-weight: 400;
  border-radius: 8px;
  border: 1px solid rgba(201, 198, 204, 0.3);
  width: 100%;
  width: fit-content;

`;

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  background: #ffffff;
  border-top: 1px solid rgba(201, 198, 204, 0.3);
  flex-shrink: 0;
`;

const AcceptButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #7200E0;
  border-radius: 20px;
  background-color: #ffffff;
  color: #7200E0;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background-color: #f5e6ff;
    color: #7200E0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  img {
    width: 16px;
    height: 16px;
  }
`;

const RejectButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ef4444;
  border-radius: 20px;
  background-color: #ffffff;
  color: #ef4444;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background-color: #ffecec;
    color: #ef4444;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  img {
    width: 16px;
    height: 16px;
  }
`;

const EmptyStateMessage = styled.div`
  color: #1e252a;
  font-size: 16px;
  font-family: IBM Plex Sans;
  font-weight: 400;
  text-align: center;
  padding: 40px 20px;
  opacity: 0.7;
`;
